import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { getAllQuestions } from '../../../../util/DatabaseUtil';
import { Circles } from 'react-loading-icons';
import { jwtDecode } from 'jwt-decode';
import { ShieldAlert } from 'lucide-react';

// Star component
const Star = ({ filled, halfFilled, onClick, onMouseEnter, onMouseLeave }) => (
  <span
    className={`relative cursor-pointer text-5xl sm:text-6xl drop-shadow-md transition-all duration-300`}
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
  >
    {halfFilled ? (
      <span className="relative">
        <span className="absolute overflow-hidden w-1/2 text-yellow-400">
          ★
        </span>
        <span className="text-gray-500">★</span>
      </span>
    ) : (
      <span className={`${filled ? 'text-yellow-400' : 'text-gray-500'}`}>
        ★
      </span>
    )}
  </span>
);

const Ratingbox = () => {
  const { id, ratingId } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const productId = queryParams.get('product');

  const server_url = process.env.REACT_APP_API_URI;
  const [questionsWithRatings, setQuestionsWithRatings] = useState([]);
  const [hoverRatings, setHoverRatings] = useState({});
  const [loading, setLoading] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [verifiedToken, setVerifiedToken] = useState(null);
  const [isOwner, setIsOwner] = useState(false);

  const [sessionRatingId, setSessionRatingId] = useState(ratingId || null);

  const businessId = id;

  useEffect(() => {
    // Check if we came from a verified QR code
    const storedVerification = localStorage.getItem('verifiedPurchase');
    if (storedVerification) {
      try {
        const data = JSON.parse(storedVerification);
        if (data.businessId === id) {
          setIsVerified(true);
          setVerifiedToken(data.token);
        }
      } catch (err) {
        console.error('Error parsing verified purchase:', err);
      }
    }

    // Check if the user is the owner of this business
    const token = localStorage.getItem('loginToken');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        if (decoded.businessId === id) {
          setIsOwner(true);
        }
      } catch (err) {
        console.error('Error decoding token:', err);
      }
    }
  }, [id]);

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        setLoading(true);
        const allQuestions = await getAllQuestions(id);
        if (allQuestions?.questions?.length > 0) {
          const questionsWithInitialRatings = allQuestions.questions.map(
            (q) => ({
              ...q,
              userRating: 0,
            }),
          );
          setQuestionsWithRatings(questionsWithInitialRatings);
        }
      } catch (error) {
        console.error('Failed to fetch questions:', error);
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchQuestions();
  }, [id]);

  const handleRating = (questionId, ratingValue) => {
    setQuestionsWithRatings((prev) =>
      prev.map((q) =>
        q._id === questionId ? { ...q, userRating: ratingValue } : q,
      ),
    );
  };

  const handleMouseEnter = (questionId, value) =>
    setHoverRatings((prev) => ({ ...prev, [questionId]: value }));

  const handleMouseLeave = (questionId) =>
    setHoverRatings((prev) => {
      const updated = { ...prev };
      delete updated[questionId];
      return updated;
    });

  const handleStarClick = async (e, questionId, starIndex) => {
    const { left, width } = e.currentTarget.getBoundingClientRect();
    const clickPosition = e.clientX - left;
    const isHalf = clickPosition < width / 2;
    const rating = isHalf ? starIndex - 0.5 : starIndex;

    // Immediately update local UI state
    handleRating(questionId, rating);

    // Find the current question details for the backend payload
    const qDetails = questionsWithRatings.find((q) => q._id === questionId);
    if (!qDetails || !businessId) return;

    // We will auto-save this single rating explicitly
    const ratingsToSubmit = {
      businessId,
      productId,
      ratingId: sessionRatingId,
      ratings: [
        {
          questionId: qDetails._id,
          rating: rating,
          question: qDetails.question,
        },
      ],
      isVerifiedPurchase: isVerified,
    };

    try {
      const response = await fetch(`${server_url}/businessRating/rate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ratingsToSubmit),
      });

      const data = await response.json();

      if (response.ok && data.ratingId) {
        // As soon as the backend provisions the specific ObjectId(), bind to it
        // so clicking more stars just upserts exactly to that document.
        if (!sessionRatingId) {
          setSessionRatingId(data.ratingId);
        }
      }
    } catch (err) {
      console.error('Failed to auto-save rating:', err);
    }
  };

  const handleSubmit = async () => {
    // If they never clicked a single star
    if (!sessionRatingId) {
      alert('Please answer at least one question before submitting.');
      return;
    }

    setLoading(true);
    try {
      // The ratings were already auto-saved upon every star click securely!
      // We just need to mark the token true natively and show the success screen.
      setShowThankYou(true);
      setQuestionsWithRatings([]);

      if (isVerified && verifiedToken) {
        await fetch(`${server_url}/qr/mark-used`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: verifiedToken }),
        });
        localStorage.removeItem('verifiedPurchase');
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col pt-8 px-4 sm:px-6 pb-20 items-center">
      <div className="w-full max-w-lg mb-8 text-center flex flex-col items-center">
        <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-3">
          Share Your Experience
        </h2>
        {isVerified && (
          <span className="inline-flex items-center bg-green-500/20 text-green-400 border border-green-500/30 text-sm font-semibold px-4 py-1.5 rounded-full shadow-sm backdrop-blur-sm">
            <svg
              className="w-4 h-4 mr-2"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 8.207-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L9 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414Z" />
            </svg>
            Verified Purchase
          </span>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-48">
          <Circles stroke="#60A5FA" fill="#60A5FA" />
        </div>
      ) : showThankYou ? (
        <div className="text-center p-10 bg-gray-800/60 backdrop-blur-xl border border-gray-700/50 shadow-2xl rounded-3xl w-full max-w-md animate-fade-in-up">
          <div className="w-24 h-24 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
            <svg
              className="w-12 h-12 text-green-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth="2"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h3 className="text-3xl font-bold text-white mb-3">Thank You!</h3>
          <p className="text-gray-400 text-lg">
            Your feedback is incredibly valuable to us and our community.
          </p>
        </div>
      ) : questionsWithRatings.length > 0 ? (
        <div className="w-full max-w-2xl">
          {/* Helpful Reassurance Header */}
          <div className="bg-blue-900/40 border border-blue-500/30 rounded-2xl p-6 sm:p-8 mb-10 text-center shadow-lg backdrop-blur-md">
            <h3 className="text-xl sm:text-2xl font-bold text-blue-200 mb-3 tracking-wide">
              Your Voice Matters!
            </h3>
            <p className="text-blue-100/90 text-sm sm:text-base leading-relaxed">
              Hey there! Feel free to skip anything. You{' '}
              <strong className="text-white">do not</strong> need to answer
              every question. Just rate the ones you think are most relevant,
              and scroll past what doesn't matter to you!
            </p>
          </div>

          <div className="space-y-8">
            {questionsWithRatings.map((question, index) => (
              <div
                key={question._id}
                className="bg-gray-800/80 backdrop-blur-2xl rounded-[2rem] p-6 sm:p-8 shadow-xl border border-gray-700/50 flex flex-col items-center justify-center transition-all hover:border-gray-600/80 duration-300"
              >
                <div className="w-full flex justify-between items-center mb-6">
                  <span className="text-xs sm:text-sm font-bold uppercase tracking-wider text-gray-400">
                    Question {index + 1}
                  </span>
                  <div className="px-3 py-1 bg-gray-900/80 rounded-full shadow-inner border border-gray-700 flex items-baseline gap-1">
                    <span className="text-lg font-bold text-white">
                      {question.userRating || 0}
                    </span>
                    <span className="text-sm font-semibold text-gray-500">
                      / 5
                    </span>
                  </div>
                </div>

                <h3 className="text-xl sm:text-2xl font-bold text-center text-white mb-8 leading-snug drop-shadow-sm w-full">
                  {question.question}
                </h3>

                <div className="flex justify-center flex-wrap gap-2 transition-transform">
                  {[1, 2, 3, 4, 5].map((star) => {
                    const hoverValue = hoverRatings[question._id];
                    const effectiveRating =
                      hoverValue !== undefined
                        ? hoverValue
                        : question.userRating;

                    return (
                      <div
                        key={star}
                        onClick={(e) => handleStarClick(e, question._id, star)}
                        onMouseEnter={(e) => {
                          const { left, width } =
                            e.currentTarget.getBoundingClientRect();
                          const isHalf = e.clientX - left < width / 2;
                          handleMouseEnter(
                            question._id,
                            isHalf ? star - 0.5 : star,
                          );
                        }}
                        onMouseLeave={() => handleMouseLeave(question._id)}
                        className="hover:scale-110 active:scale-95 transition-transform duration-150"
                      >
                        <Star
                          filled={effectiveRating >= star}
                          halfFilled={effectiveRating === star - 0.5}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-center mt-12">
            <button
              onClick={handleSubmit}
              disabled={loading}
              className={`py-4 px-10 rounded-[2rem] font-bold text-xl transition-all shadow-xl focus:outline-none focus:ring-4 focus:ring-blue-500/50 w-full sm:w-auto ${
                loading
                  ? 'bg-gray-700/50 text-gray-500 border border-gray-700 cursor-not-allowed'
                  : 'bg-gradient-to-r from-[#F2994A] to-[#F2C94C] hover:from-[#e38c41] hover:to-[#e1bb44] text-[#0E1834] hover:shadow-orange-500/25 hover:-translate-y-1'
              }`}
            >
              {loading ? 'Submitting Review...' : 'Submit Ratings'}
            </button>
          </div>
        </div>
      ) : isOwner ? (
        <div className="text-center p-12 bg-red-500/10 border border-red-500/20 rounded-3xl w-full max-w-lg mt-12 animate-fade-in">
          <ShieldAlert className="w-16 h-16 text-red-500 mx-auto mb-6" />
          <h3 className="text-2xl font-black text-white mb-4 uppercase tracking-tighter">
            Access Denied
          </h3>
          <p className="text-slate-400 text-lg leading-relaxed">
            As the owner of this business, you cannot submit ratings for your
            own profile or products. Please use the business dashboard to manage
            your reviews.
          </p>
          <button
            onClick={() => (window.location.href = '/business-profile')}
            className="mt-8 px-8 py-3 bg-red-500 hover:bg-red-400 text-white font-black rounded-xl transition-all shadow-lg shadow-red-500/20"
          >
            Back to Dashboard
          </button>
        </div>
      ) : (
        <p className="text-center text-gray-400 text-xl font-medium mt-10">
          No rating questions found for this profile.
        </p>
      )}
    </div>
  );
};

export default Ratingbox;
